// Schlanker Fetch-Wrapper. Cookies werden mitgesendet (credentials: include).
// Vite proxied /api → http://localhost:3000 im Dev-Setup.

import type {
  UploadMeta, DraftResponse, SubmitResponse, OnboardingDraft, SubmitInput,
  NachreichungListResponse, NachreichungUploadResponse, MeResponse,
  QuestionnaireResponse, CaseIntakeSubmit,
} from '@gql/shared-types';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public details?: unknown) {
    super(`${status} ${code}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/public${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'include',
  });
  let body: unknown = null;
  try { body = await res.json(); } catch {}
  if (!res.ok) {
    const b = body as { error?: string; issues?: unknown } | null;
    throw new ApiError(res.status, b?.error ?? 'unknown_error', b?.issues);
  }
  return body as T;
}

async function multipart<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`/api/public${path}`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  let body: unknown = null;
  try { body = await res.json(); } catch {}
  if (!res.ok) {
    const b = body as { error?: string } | null;
    throw new ApiError(res.status, b?.error ?? 'unknown_error');
  }
  return body as T;
}

export const api = {
  // Auth
  register: (data: object) => request<{ ok: true; message: string }>('/register', { method: 'POST', body: JSON.stringify(data) }),
  verify: (token: string) => request<{ ok: true }>(`/verify?token=${encodeURIComponent(token)}`),
  loginRequest: (data: object) => request<{ ok: true; message: string }>('/login', { method: 'POST', body: JSON.stringify(data) }),
  loginConsume: (token: string) => request<{ ok: true }>(`/login?token=${encodeURIComponent(token)}`),
  inviteConsume: (token: string) => request<{ ok: true }>(`/invite?token=${encodeURIComponent(token)}`),
  me: () => request<MeResponse>('/me'),
  logout: () => request<{ ok: true }>('/logout', { method: 'POST' }),

  // Onboarding
  getQuestionnaire: () => request<QuestionnaireResponse | null>('/questionnaire'),
  getDraft: () => request<DraftResponse | null>('/draft'),
  putDraft: (draft: OnboardingDraft) => request<{ ok: true }>('/draft', {
    method: 'PUT',
    body: JSON.stringify(draft),
  }),
  uploadFiles: async (files: File[], kategorie: string): Promise<UploadMeta[]> => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    fd.append('kategorie', kategorie);
    const res = await multipart<{ uploads: UploadMeta[] }>('/uploads', fd);
    return res.uploads;
  },
  deleteUpload: (id: string) => request<{ ok: true }>(`/uploads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  submit: (data: SubmitInput) => request<SubmitResponse>('/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // Fall-Intake-Submit (getrennter /intake-Flow): Fragebogen + Vollmacht.
  submitIntake: (data: CaseIntakeSubmit) => request<SubmitResponse>('/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Nachreichungen (post-Submit / nach Bookings-Termin)
  getNachreichungen: () => request<NachreichungListResponse>('/uploads/nachreichung'),
  uploadNachreichungen: async (files: File[]): Promise<NachreichungUploadResponse> => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    return multipart<NachreichungUploadResponse>('/uploads/nachreichung', fd);
  },
  deleteNachreichung: (id: string) =>
    request<{ ok: true }>(`/uploads/nachreichung/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
